import React from "react";

export interface TabsIndicatorPosition {
  left: number;
  width: number;
  rise: number;
}

/* The horizontal indicator slides between tabs (ADR-0092): one span on the
   tablist's bottom edge, measured from the active tab. Until a measurement
   lands (or when it fails — jsdom, an unlaid-out tablist) it stays hidden
   rather than flashing at x=0. Because the tablist WRAPS (PB-5332), `rise`
   lifts the span from the tablist's bottom edge up to the active tab's own
   row — zero while everything fits on one row. */
export function useTabsIndicator({
  vertical,
  selectedItemIndex,
  items,
  tabRefs,
}: {
  vertical: boolean;
  selectedItemIndex: number;
  items: readonly unknown[];
  tabRefs: React.MutableRefObject<Array<HTMLButtonElement | null>>;
}): {
  tablistRef: React.RefObject<HTMLDivElement>;
  indicator: TabsIndicatorPosition | null;
} {
  const tablistRef = React.useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] =
    React.useState<TabsIndicatorPosition | null>(null);
  React.useLayoutEffect(() => {
    if (vertical) return;
    const measure = (): void => {
      const tab =
        selectedItemIndex >= 0 ? tabRefs.current[selectedItemIndex] : null;
      if (!tab || tab.offsetWidth === 0) {
        setIndicator((prev) => (prev === null ? prev : null));
        return;
      }
      const { offsetLeft: left, offsetWidth: width } = tab;
      const rise = Math.max(
        0,
        (tablistRef.current?.offsetHeight ?? 0) -
          (tab.offsetTop + tab.offsetHeight),
      );
      setIndicator((prev) =>
        prev && prev.left === left && prev.width === width && prev.rise === rise
          ? prev
          : { left, width, rise },
      );
    };
    measure();
    const tablist = tablistRef.current;
    if (!tablist || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(tablist);
    /* Each tab too: a tab can change size while the row keeps its own (a web
       font that loads after the first measure, a label that grows), and a tab
       that grows moves every tab after it (d14). */
    for (const tab of tabRefs.current) if (tab) observer.observe(tab);
    return () => observer.disconnect();
  }, [vertical, selectedItemIndex, items, tabRefs]);
  return { tablistRef, indicator };
}
